import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { FormattedMessage } from 'react-intl';

import { PAYMENT_FREQUENCIES } from '@ratehub/documents/mtg-journey-document/stable';
import { LegacyCityPropType, Config, PROVINCE } from '@ratehub/base-ui';

import { DEBT_PAYMENT_FIELD_NAMES } from '../definitions/AffordabilityMultiInputFields';
import AffordabilityStepInputs from './AffordabilityStepInputs';
import AffordabilityStepMortgageScenario from './AffordabilityStepMortgageScenario';
import AffordabilityStepCashNeeded from './AffordabilityStepCashNeeded';
import AffordabilityStepMonthlyExpenses from './AffordabilityStepMonthlyExpenses';
import MortgageCalculator from './MortgageCalculator';


function AffordabilityCalculator({
    initialAnnualPropertyTax,
    initialCity,
    initialProvinceCode,
    isProductReferralDisabled = false,
    ...otherProps
}) {
    const [ isCashNeededEnabled, setIsCashNeededEnabled ] = useState(false); // flag to enable cash needed page and monthly page
    const [ inputStepTitle, setInputStepTitle ] = useState(
        <FormattedMessage
            id="mtg-calculator.Affordability.inputStepTitle-incomplete"
            defaultMessage="Calculate your maximum affordability"
        />,
    );

    function handleStepInputsComplete() {
        // Change the step title when completed
        setInputStepTitle(
            <FormattedMessage
                id="mtg-calculator.Affordability.inputStepTitle-completed"
                defaultMessage="Your maximum home price"
            />,
        );

        // Once Step Input is complete, we can enable cash needed and monthly page, and not unmount them when go back page 1
        !isCashNeededEnabled && setIsCashNeededEnabled(true);
    }

    return (
        <MortgageCalculator
            title={(
                <FormattedMessage
                    id="mtg-calculator.Affordability.calculatorTitle"
                    defaultMessage="Ratehub.ca’s mortgage affordability calculator"
                />
            )}
            onSaveDocument={saveFormData}
            isSaveEnabled={Config.ENABLE_SAVING_DATA_FOR_CALCULATORS}
            {...otherProps}
        >
            <AffordabilityStepInputs
                title={inputStepTitle}
                ctaText={(
                    <FormattedMessage
                        id="mtg-calculator.Affordability.inputCTAText"
                        defaultMessage="How much can I afford"
                    />
                )}
                stepNumber={1}
                isInitiallyExpanded
                initialValues={{
                    city: initialCity,
                    province: initialProvinceCode,
                    propertyTax: initialAnnualPropertyTax,
                }}
                ctaDataName="AffordabilityCalculator.InputStep-continue"
                onConfirmComplete={handleStepInputsComplete}
                accordionDataName="calculator-step[1].mtg-affordabilityCalculator-accordion"
            />

            <AffordabilityStepMortgageScenario
                title={(
                    <FormattedMessage
                        id="mtg-calculator.Affordability.mortgageScenarioStepTitle"
                        defaultMessage="Build your personalized mortgage scenario to determine your mortgage payment"
                    />
                )}
                stepNumber={2}
                accordionDataName="calculator-step[2].mtg-affordabilityCalculator-accordion"
                isProductReferralDisabled={isProductReferralDisabled}
            />

            <If condition={isCashNeededEnabled}>
                <AffordabilityStepCashNeeded
                    stepNumber={3}
                    title={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityCashNeeded.cashNeededTitle"
                            defaultMessage="Cash needed"
                        />
                    )}
                    accordionDataName="calculator-step[3].mtg-affordabilityCalculator-accordion"
                />
                <AffordabilityStepMonthlyExpenses
                    stepNumber={4}
                    title={(
                        <FormattedMessage
                            id="mtg-calculators.AffordabilityMonthlyExpenses.title"
                            defaultMessage="Monthly Expenses"
                        />
                    )}
                    accordionDataName="calculator-step[4].mtg-affordabilityCalculator-accordion"
                />
            </If>
        </MortgageCalculator>
    );
}

AffordabilityCalculator.propTypes = {
    initialAnnualPropertyTax: PropTypes.number,
    initialCity: LegacyCityPropType,
    initialProvinceCode: PropTypes.oneOf(Object.values(PROVINCE)),
    isProductReferralDisabled: PropTypes.bool,
};


function saveFormData(document, values) {
    // This a work around for saving nested arrays (they're not supported using our update method)
    // therefore creating a placeholder contact ahead of time
    if ((document?.journey?.calculators?.contacts?.length ?? 0) === 0) {
        document?.journey?.update({
            calculators: {
                contacts: [
                    {
                        applicationRank: 1,
                        employment: [ { address: {} } ],
                        expenses: [],
                    },
                    {
                        applicationRank: 2,
                        employment: [ { address: {} } ],
                    },
                ],
            },
        });
    }

    // NOTE: creating an object with keys as indices so that partiallyUpdateModel
    // function will merge/combine the arrays instead of overwriting it
    document?.journey?.update({
        calculators: {
            contacts: {
                0: {
                    employment: {
                        0: {
                            income: values?.yourIncome,
                        },
                    },
                    expenses: {
                        0: {
                            frequency: PAYMENT_FREQUENCIES.MONTHLY,
                            type: DEBT_PAYMENT_FIELD_NAMES.CREDIT_CARD,
                            value: values?.creditCardPayment,
                        },
                        1: {
                            frequency: PAYMENT_FREQUENCIES.MONTHLY,
                            type: DEBT_PAYMENT_FIELD_NAMES.CAR,
                            value: values?.carPayment,
                        },
                        2: {
                            frequency: PAYMENT_FREQUENCIES.MONTHLY,
                            type: DEBT_PAYMENT_FIELD_NAMES.OTHER,
                            value: values?.otherExpenses,
                        },
                    },
                },
                1: {
                    employment: {
                        0: {
                            income: values?.coApplicantIncome,
                        },
                    },
                },
            },
            mortgage: {
                downPayment: values?.downPayment?.dollars,
            },
            property: {
                address: {
                    city: values?.futureHomeLocation?.name,
                    province: values?.futureHomeLocation?.province?.code,
                },
                propertyTax: values?.propertyTax,
                condoFees: values?.condoFees,
                monthlyHeat: values?.heatingCosts,
            },
        },
    });
}

export default observer(AffordabilityCalculator);
